import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

const regatesRoyalesCannes: ChauffeurPage = {
  slug: "regates-royales-cannes",
  title: "Chauffeur privé Régates Royales de Cannes – VTC",
  subtitle:
    "Chauffeur privé pendant les Régates Royales de Cannes : accès au vieux port et au Yacht Club, transferts depuis l'aéroport de Nice et soirées.",
  keywords: [
    "chauffeur régates royales cannes",
    "vtc régates royales",
    "chauffeur privé cannes yachts classiques",
  ],
  hero: {
    title: "Chauffeur privé pendant les Régates Royales de Cannes",
    subtitle:
      "Yachts classiques dans la baie : un chauffeur dédié quand le vieux port s'anime et que le stationnement se fait rare à Cannes.",
    keywords: [
      "chauffeur Régates Royales de Cannes",
      "VTC vieux port Cannes",
      "transfert aéroport Nice Cannes",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé pendant les Régates Royales de Cannes",
    badges: ["Accueil aéroport", "Accès vieux port", "Soirées & retours"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur pendant les Régates Royales",
    items: [
      {
        title: "Vieux port et Croisette maîtrisés",
        description:
          "Le village des Régates s'installe au vieux port. Le chauffeur connaît les déposes autorisées les plus proches du Yacht Club et de la Croisette.",
      },
      {
        title: "Accueil aéroport de Nice",
        description:
          "Nice Côte d'Azur est à moins de 30 km. Accueil pancarte, suivi de vol et transfert direct vers votre hôtel cannois.",
      },
      {
        title: "Stationnement rare géré",
        description:
          "Fin septembre, se garer près du port de Cannes est compliqué : le chauffeur dépose au plus près et repart.",
      },
      {
        title: "5,0/5 sur 38 avis Google",
        description:
          "Ponctualité, discrétion et véhicules soignés : la régularité qui vaut à KS Rent Services une note de 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur pendant les Régates Royales de Cannes",
    services: [
      {
        title: "Accueil aéroport de Nice",
        text: "Transfert Nice ↔ Cannes avec suivi de vol pendant la semaine des régates.",
      },
      {
        title: "Accès au vieux port & Yacht Club",
        text: "Dépose au plus près des quais, du village des régates et du Yacht Club de Cannes.",
      },
      {
        title: "Mise à disposition journée",
        text: "Chauffeur dédié pour enchaîner port, déjeuners sur la Croisette et remises des prix.",
      },
      {
        title: "Soirées & retours de nuit",
        text: "Dîners et soirées à Cannes ou dans l'arrière-pays, retour tardif garanti.",
      },
      {
        title: "Navettes invités & équipages",
        text: "Coordination multi-véhicules pour invités, armateurs et équipages.",
      },
    ],
  },
  locations: {
    heading: "Points clés pendant les Régates Royales",
    items: [
      {
        name: "Vieux port de Cannes",
        description:
          "Quai Saint-Pierre, village des régates et départs en baie.",
      },
      {
        name: "Yacht Club de Cannes",
        description:
          "Organisation des Régates Royales et point de rendez-vous.",
      },
      {
        name: "La Croisette",
        description: "Palaces, plages privées et hôtels du front de mer.",
      },
      {
        name: "Aéroport de Nice Côte d'Azur",
        description: "À moins de 30 km, terminaux 1 et 2.",
      },
      {
        name: "Le Suquet & arrière-pays",
        description: "Vieille ville et dîners hors du centre.",
      },
    ],
  },
  generic: {
    heading: "Les Régates Royales de Cannes vues d'un chauffeur",
    subheading:
      "Chaque année à la fin de septembre, les yachts classiques se retrouvent dans la baie de Cannes autour du vieux port.",
    paragraphs: [
      "Chaque automne, fin septembre, les Régates Royales rassemblent de somptueux yachts classiques dans la baie de Cannes. Le village des régates s'installe au vieux port, près du Yacht Club, et le centre se remplit : le stationnement devient rare et les abords des quais sont encombrés.",
      "L'aéroport de Nice Côte d'Azur est à moins de trente kilomètres. Avec le suivi de vol et l'accueil pancarte, le transfert vers votre hôtel de la Croisette se fait sans attente, même en pleine affluence.",
      "Entre régates, déjeuners sur la Croisette et soirées, le rythme est dense. La mise à disposition garde le même chauffeur toute la journée et assure les retours de nuit après les remises des prix.",
    ],
    bullets: [
      "Accès au vieux port",
      "Aéroport à moins de 30 km",
      "Retours de nuit",
    ],
  },
  process: {
    heading: "Réserver un chauffeur pour les Régates Royales de Cannes",
    steps: [
      "Indiquez vos dates de régates, votre hôtel et vos horaires.",
      "Nous calons les déposes selon l'accès au vieux port et l'affluence sur la Croisette.",
      "Le chauffeur se présente en tenue, avec suivi de vol pour vos arrivées à Nice.",
      "Transfert direct ou mise à disposition pour couvrir port, déjeuners et soirées.",
    ],
  },
  faq: [
    {
      q: "Comment réserver un chauffeur privé pendant les Régates Royales de Cannes ?",
      a: "Réservez tôt : fin septembre, Cannes est très demandée et la semaine des régates attire de nombreux invités. Précisez vos dates, votre hôtel et vos horaires pour que nous bloquions un chauffeur dédié.",
    },
    {
      q: "Le chauffeur peut-il m'approcher du vieux port et du Yacht Club ?",
      a: "Oui. Le village des régates occupe le vieux port et les abords sont encombrés. Le chauffeur vous dépose au point autorisé le plus proche du Yacht Club de Cannes, puis vous reprend à l'endroit convenu.",
    },
    {
      q: "Gérez-vous les transferts depuis l'aéroport de Nice ?",
      a: "Oui, avec suivi de vol et accueil pancarte. L'aéroport de Nice Côte d'Azur est à moins de 30 km de Cannes : le transfert vers votre hôtel de la Croisette est rapide, même en haute saison.",
    },
    {
      q: "Le stationnement est-il compliqué à Cannes pendant les régates ?",
      a: "Oui. Fin septembre, les parkings du centre saturent et les abords du port sont encombrés. Le chauffeur vous évite de chercher une place : il dépose, patiente ou revient à l'heure convenue.",
    },
    {
      q: "Assurez-vous les retours tardifs après les soirées ?",
      a: "Oui. Les dîners et remises des prix se terminent tard : le chauffeur patiente et garantit votre retour direct à l'hôtel, à Cannes ou ailleurs sur la Riviera.",
    },
    {
      q: "Quels véhicules proposez-vous ?",
      a: "Berlines, SUV et vans avec chauffeur selon la taille de votre groupe, pour un trajet confortable entre l'hôtel, le port et les soirées.",
    },
    {
      q: "Pouvez-vous coordonner plusieurs véhicules pour un équipage ou un groupe ?",
      a: "Oui. Nous organisons des navettes multi-véhicules pour les invités, armateurs et équipages, avec le même niveau de ponctualité pour tout le monde.",
    },
  ],
  relatedLinks: [
    {
      label: "Chauffeur Cannes, tarifs et trajets",
      href: "/chauffeur/cannes",
    },
    {
      label: "VTC aéroport de Nice",
      href: "/chauffeur/aeroport",
    },
    {
      label: "Transferts sur la Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
    {
      label: "Louer une voiture Cannes",
      href: "/location/location-voiture-cannes",
    },
  ],
  cta: {
    title: "Votre chauffeur pour les Régates Royales de Cannes",
    text: "Accueil aéroport, accès au vieux port et retours de nuit : réservez un chauffeur dédié pour la semaine des régates.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    title: "Chauffeur privé Régates Royales Cannes | KS Rent",
    description:
      "Chauffeur privé pendant les Régates Royales de Cannes : transfert aéroport de Nice, accès au vieux port et au Yacht Club, mise à disposition et retours de nuit.",
    canonical: "/chauffeur/regates-royales-cannes",
  },
};

export default regatesRoyalesCannes;
